@if (link) {
  <!-- The picker strip's pattern (c0086de5, mobile 90912be2): no box; a "LINKED FEATURE" label row with the "…" on it,
       as on LOCATION / STATION; then an optional 60px tile and the text lines. -->
  <section class="design-context" [class.design-context-collapsed]="collapsed" data-testid="design-context-control" aria-label="Linked feature">
    <div class="design-context-header" data-testid="design-context-label-row">
      <div class="design-context-label text-type-13" data-testid="design-context-label">{{ isSheetLink ? 'Linked sheet' : 'Linked feature' }}</div>
      @if (editable) {
        <button type="button" class="design-context-menu-trigger" data-testid="design-context-menu" aria-label="Design context actions"
          [matMenuTriggerFor]="designContextMenu" (click)="$event.stopPropagation()">
          <mat-icon aria-hidden="true">more_horiz</mat-icon>
        </button>
        <!-- Ink rows (red only in confirms): Edit location when the host offers it, Mark up the snapshot, Remove link. -->
        <mat-menu #designContextMenu="matMenu" xPosition="before">
          @if (editLocation.observed) {
            <button mat-menu-item type="button" data-testid="design-context-edit-location" (click)="editLocation.emit()">
              <mat-icon aria-hidden="true">edit_location_alt</mat-icon>
              Edit location
            </button>
          }
          @if (canMarkUpSheet) {
            <!-- W71: the record's own strokes on the sheet, without going through the picker -->
            <button mat-menu-item type="button" data-testid="design-context-menu-sheet-markup" (click)="openSheetMarkup()">
              <mat-icon aria-hidden="true">edit</mat-icon>
              {{ sheetHasMarkup ? 'Edit markup' : 'Mark up' }}
            </button>
          }
          @if (canMarkUp) {
            <button mat-menu-item type="button" data-testid="design-context-menu-markup" (click)="openMarkup()">
              <mat-icon aria-hidden="true">edit</mat-icon>
              {{ link.snapshot?.has_markup ? 'Edit markup' : 'Mark up' }}
            </button>
          }
          <button mat-menu-item type="button" data-testid="design-context-remove" (click)="requestRemove()">
            <mat-icon svgIcon="trash" aria-hidden="true" />
            Remove link
          </button>
        </mat-menu>
      }
    </div>

    <div class="design-context-info">
      <!-- The tile only where the host's location slot does not show the capture (spec addendum §11). -->
      @if (!collapsed && !isSheetCrop) {
        @if (hasCapture) {
          <!-- Summaries: the crew's capture; clicking it opens the full picture in the viewer. -->
          <div class="design-context-tile" data-testid="design-context-capture">
            <app-design-capture-preview [link]="link" height="60px" [viewOnClick]="true" [showBadges]="false" />
          </div>
        } @else if (snapshotStatus === 'ready' && snapshotSrc) {
          <!-- The design snapshot: an editable card marks it up (the picker tile's action); read-only opens the viewer. -->
          <div class="design-context-tile" data-testid="design-context-snapshot">
            @if (canMarkUp) {
              <button type="button" class="design-context-tile-button" data-testid="design-context-markup" aria-label="Mark up the map" (click)="openMarkup()">
                <img [src]="snapshotSrc" alt="Design snapshot around the pin" loading="lazy" />
              </button>
            } @else {
              <button type="button" class="design-context-tile-button" data-testid="design-context-snapshot-view" aria-label="View design snapshot"
                (click)="viewSnapshot($event)">
                <img [src]="snapshotSrc" [alt]="isSheetCrop ? 'The sheet around the point' : 'Design snapshot around the pin'" loading="lazy" />
              </button>
            }
          </div>
        }
      }

      <div class="design-context-text">
        <div class="design-context-title" data-testid="design-context-title" [attr.title]="lines.title">{{ lines.title }}</div>
        @if (lines.detail) {
          <div class="design-context-line text-type-14" data-testid="design-context-detail" [attr.title]="lines.detail">{{ lines.detail }}</div>
        }
        @if (badge) {
          <div class="design-context-line text-type-14" data-testid="design-context-badge">{{ badge }}</div>
        }
        @if (asBuiltStatus; as status) {
          <!-- 7.2: the record's markup is an as-built mark; its review status (independent of the record's) -->
          <div class="design-context-line">
            <span class="status-badge design-context-as-built" data-testid="design-context-as-built-status" [attr.data-status]="status"
              matTooltip="This record's markup is an as-built mark in the drawings review">As-built · {{ asBuiltStatusCopy[status] }}</span>
          </div>
        }
        @if (canOpenSheet) {
          <div class="design-context-line text-type-14">
            <button type="button" class="design-context-action" data-testid="design-context-open-sheet" (click)="openSheet($event)">Open sheet</button>
          </div>
        }
        @if (!collapsed && !isSheetCrop) {
          @if (hasCapture) {
            @if (mapMoved) {
              <div class="design-context-line text-type-14" data-testid="design-context-capture-moved">Map moved since capture</div>
            }
          } @else if (snapshotStatus === 'pending') {
            <div class="design-context-hint text-type-14" data-testid="design-context-snapshot-status" role="status">Rendering snapshot…</div>
          } @else if (snapshotStatus === 'failed') {
            <div class="design-context-hint text-type-14" data-testid="design-context-snapshot-status" role="status">
              Snapshot unavailable
              @if (canRetry) {
                · <button type="button" class="design-context-action" data-testid="design-context-retry" [disabled]="retrying" (click)="retry()">Retry</button>
              }
            </div>
          } @else if (canRetry) {
            <div class="design-context-hint text-type-14" data-testid="design-context-snapshot-status" role="status">
              No snapshot yet
              · <button type="button" class="design-context-action" data-testid="design-context-retry" [disabled]="retrying" (click)="retry()">Render snapshot</button>
            </div>
          } @else if (showNoSnapshotYet) {
            <div class="design-context-hint text-type-14" data-testid="design-context-snapshot-status" role="status">No snapshot yet</div>
          }
          @if (retryError) {
            <div class="design-context-error text-type-14" data-testid="design-context-retry-error" role="status">{{ retryError }}</div>
          }
        }
      </div>
    </div>

    @if (showSheetPreview) {
      <!-- The sheet's own page render, cropped around the point on the client (W28): no server snapshot, no PDF. Clicking opens the sheet. -->
      <button type="button" class="design-context-sheet-preview" [class.design-context-sheet-preview-small]="!isSheetCrop"
        data-testid="design-context-sheet-preview" [attr.aria-label]="sheetPreviewLabel" (click)="openSheet($event)">
        @if (sheetCrop; as crop) {
          <span class="design-context-sheet-page" [style.width.%]="crop.width * 100" [style.left.%]="crop.left * 100" [style.top.%]="crop.top * 100"
            [style.aspect-ratio]="sheetPageAspect">
            <img class="design-context-sheet-img" [src]="sheetPreview.src" [alt]="sheetPreviewLabel" loading="lazy" (load)="onSheetPageLoad($event)" />
            @if (sheetPreview.markupSrc) {
              <img class="design-context-sheet-img" data-testid="design-context-sheet-markup" [src]="sheetPreview.markupSrc" alt="" loading="lazy" />
            }
          </span>
          @if (link.sheet.point) {
            <span class="design-context-sheet-pin" data-testid="design-context-sheet-pin" [style.left.%]="crop.pinLeft * 100" [style.top.%]="crop.pinTop * 100"></span>
          }
        } @else {
          <!-- Measures the page's aspect; the crop appears once it has loaded. -->
          <img class="design-context-sheet-measure" [src]="sheetPreview.src" alt="" loading="lazy" (load)="onSheetPageLoad($event)" />
        }
      </button>
    }
  </section>
}

@if (showMarkupEditor && markupConfig) {
  <!-- The editor's colour/size inputs fire native change/input events; keep them inside this control so no host
       listener on the element ever sees them. -->
  <div class="design-context-markup-editor" (change)="$event.stopPropagation()" (input)="$event.stopPropagation()">
    <app-markup-editor [config]="markupConfig" (saved)="onMarkupSaved($event)" (closed)="closeMarkupEditor()"></app-markup-editor>
  </div>
}
